import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
} from "@mui/material";
import api from "../../utils/api";
import { useNavigate } from "react-router-dom";
import useCBTSubscription from "../../hooks/useCBTSubscription";

/* =========================================================
   ✅ Hooks/Helpers MUST be outside component
========================================================= */
function getNextWeekStartISO() {
  const now = new Date();
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diffToMonday = day === 0 ? 1 : 8 - day;
  d.setDate(d.getDate() + diffToMonday);
  return d.toISOString();
}

function useCountdown(targetISO) {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (!targetISO) {
      setRemaining(0);
      return;
    }

    const targetMs = new Date(targetISO).getTime();
    if (!Number.isFinite(targetMs)) {
      setRemaining(0);
      return;
    }

    const tick = () => {
      const sec = Math.max(0, Math.floor((targetMs - Date.now()) / 1000));
      setRemaining(sec);
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetISO]);

  const d = Math.floor(remaining / 86400);
  const h = Math.floor((remaining % 86400) / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;

  return {
    remaining,
    formatted: remaining <= 0 ? "0d 0h 0m 0s" : `${d}d ${h}h ${m}m ${s}s`,
  };
}

export default function JambRegisterPage() {
  const navigate = useNavigate();
const { loading, hasCBT } = useCBTSubscription();

  /* ===================== STATE ===================== */
  const [subjects, setSubjects] = useState([]);
  const [selected, setSelected] = useState([]);
  const [registeredSubjects, setRegisteredSubjects] = useState([]);

  const [mode, setMode] = useState("loading");
  const [submitting, setSubmitting] = useState(false);
  const [clearing, setClearing] = useState(false);


  const [resumeInfo, setResumeInfo] = useState(null);
  const [lockedInfo, setLockedInfo] = useState(null);
  const [attemptId, setAttemptId] = useState(null);

  // ✅ NEW: block this page if active exam is SUBJECT or POST_UTME
  const [blockedByOtherExam, setBlockedByOtherExam] = useState(false);
  const [blockedExamType, setBlockedExamType] = useState(null);

  // ✅ NEW: PAID-ONLY JAMB access gate (trial must be blocked)
  const [jambAccessLoading, setJambAccessLoading] = useState(true);
  const [jambAccessDenied, setJambAccessDenied] = useState(false);
  const [jambAccessMessage, setJambAccessMessage] = useState("");

  const autoRefreshRanRef = useRef(false);

  /* ===================== HELPERS ===================== */
  const isEnglish = (s) =>
    (s.code || "").toUpperCase() === "EGL" ||
    (s.name || "").toLowerCase().includes("english");

  const isPostUtme = (s) =>
    (s.code || "").toUpperCase() === "POST_UTME" ||
    (s.name || "").toLowerCase().includes("post");

  const formatTime = (sec) => {
    if (!Number.isFinite(sec)) return "--:--";
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const countdownTarget = useMemo(() => {
    if (lockedInfo?.next_week_start) return lockedInfo.next_week_start;
    if (lockedInfo?.locked) return getNextWeekStartISO();
    return null;
  }, [lockedInfo]);

  const countdown = useCountdown(countdownTarget);

  // ✅ NEW: centralized handler for PAID-ONLY block
  const denyJambAccess = (msg) => {
    setJambAccessDenied(true);
    setJambAccessMessage(
      msg ||
        "JAMB is available for paid CBT subscribers only. Trial users cannot access JAMB."
    );
    setMode("review"); // keep UI stable; we’ll show an access alert
  };

  const goToUpgrade = () => {
    navigate("/dashboard/my-subscriptions?plan=cbt", { replace: true });
  };

  /* ===================== LOAD SUBJECTS ===================== */
  const loadSubjects = async () => {
    const res = await api.get("/api/student/subjects");
    const raw = Array.isArray(res.data?.subjects) ? res.data.subjects : [];
    setSubjects(
      raw
        .filter((s) => s && !isPostUtme(s))
        .map((s) => ({
          id: Number(s.id ?? s.subject_id),
          name: s.name,
          code: s.code,
        }))
        .filter((s) => Number.isFinite(s.id))
    );
  };

  /* ===================== INITIAL LOAD ===================== */
  useEffect(() => {
    const init = async () => {
      try {
        setMode("loading");
        setJambAccessLoading(true);
        setJambAccessDenied(false);
        setJambAccessMessage("");

        // ✅ IMPORTANT:
        // This call should be protected by backend "requireCBTSubscription" (PAID ONLY).
        // If backend returns 403 CBT_SUBSCRIPTION_REQUIRED, trial users are blocked here.
        const reg = await api.get("/jamb/my-registration");

        // If we reached here, user has JAMB access (paid CBT)
        setJambAccessLoading(false);

        if (reg.data?.attempt_id) setAttemptId(reg.data.attempt_id);

        if (reg.data?.registered && reg.data?.status === "completed") {
          setRegisteredSubjects(reg.data.subjects || []);
          setLockedInfo({
            locked: true,
            next_week_start: reg.data?.next_week_start || null,
          });
          setMode("review");
        } else if (reg.data?.registered) {
          setRegisteredSubjects(reg.data.subjects || []);
          setMode("review");
        } else {
          await loadSubjects();
          setMode("register");
        }

        const active = await api.get("/exam/active");

        // ✅ JAMB-ONLY: if active exam exists and is NOT JAMB, block ALL start/resume buttons here
        if (active.data?.hasActiveExam && active.data.exam?.exam_type) {
          const t = String(active.data.exam.exam_type).toUpperCase();

          if (t !== "JAMB") {
            setBlockedByOtherExam(true);
            setBlockedExamType(t);
            setResumeInfo(null); // ensure no JAMB resume banner shows
            return; // stop here — user must finish other exam elsewhere
          }
        }

        // ✅ Only show resume info if active exam is JAMB
        if (
          active.data?.hasActiveExam &&
          String(active.data.exam?.exam_type || "").toUpperCase() === "JAMB"
        ) {
          const subj = reg.data?.subjects?.find(
            (s) => s.id === active.data.exam.subject_id
          );

          // ✅ UPGRADE: resume subject index
          setResumeInfo({
            subjectName: subj?.name || "Current Subject",
            remainingSeconds: active.data.exam.remaining_seconds,
            subjectIndex: active.data.exam.jamb_subject_index,
            totalSubjects: active.data.exam.total_subjects || 4,
            examId: active.data.exam.id,
          });
        }
      } catch (err) {
        // ✅ NEW: PAID-ONLY enforcement on frontend based on backend response
        const status = err?.response?.status;
        const code = err?.response?.data?.code;
        const msg = err?.response?.data?.message;

        // Backend should return CBT_SUBSCRIPTION_REQUIRED for trial users (paid-only gate)
        if (status === 403 && code === "CBT_SUBSCRIPTION_REQUIRED") {
          setJambAccessLoading(false);
          denyJambAccess(msg);
          return;
        }

        // Also handle generic subscription required (in case server returns this)
        if (status === 403 && (code === "SUBSCRIPTION_REQUIRED" || code)) {
          setJambAccessLoading(false);
          denyJambAccess(msg);
          return;
        }

        // fallback: keep old behavior
        try {
          await loadSubjects();
          setMode("register");
        } catch {
          // ignore
        } finally {
          setJambAccessLoading(false);
        }
      }
    };

    init();
  }, []);

  /* ===================== AUTO REFRESH ===================== */
  useEffect(() => {
    if (!lockedInfo?.locked) return;
    if (!countdownTarget) return;
    if (countdown.remaining !== 0) return;
    if (autoRefreshRanRef.current) return;

    autoRefreshRanRef.current = true;

    const refresh = async () => {
      try {
        // ✅ If JAMB access denied, do not refresh
        if (jambAccessDenied) return;

        const reg = await api.get("/jamb/my-registration");

        if (reg.data?.registered && reg.data?.status === "completed") {
          setRegisteredSubjects(reg.data.subjects || []);
          setLockedInfo({
            locked: true,
            next_week_start: reg.data?.next_week_start || null,
          });
          setMode("review");
          return;
        }

        if (reg.data?.registered) {
          setRegisteredSubjects(reg.data.subjects || []);
          setLockedInfo(null);
          setMode("review");
          return;
        }

        setLockedInfo(null);
        setRegisteredSubjects([]);
        setSelected([]);
        await loadSubjects();
        setMode("register");
      } catch {
        autoRefreshRanRef.current = false;
      }
    };

    refresh();
  }, [countdown.remaining, countdownTarget, lockedInfo, jambAccessDenied]);

  useEffect(() => {
    autoRefreshRanRef.current = false;
  }, [countdownTarget]);

  /* ===================== TOGGLE ===================== */
  const toggle = (subject) => {
    if (lockedInfo?.locked) return;
    if (isEnglish(subject)) return;

    // ✅ PAID-ONLY guard
    if (jambAccessDenied) return;

    setSelected((prev) =>
      prev.includes(subject.id)
        ? prev.filter((x) => x !== subject.id)
        : prev.length < 3
        ? [...prev, subject.id]
        : prev
    );
  };

  /* ===================== REGISTER ===================== */
  const register = async () => {
    if (lockedInfo?.locked) return;

    // ✅ PAID-ONLY guard
    if (jambAccessDenied) {
      toast.error("JAMB is for paid CBT subscribers only.");
      goToUpgrade();
      return;
    }

    if (selected.length !== 3) {
      toast.warning("Select exactly 3 subjects (English is compulsory).");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/jamb/register", { subject_ids: selected });
      const reg = await api.get("/jamb/my-registration");
      if (reg.data?.attempt_id) setAttemptId(reg.data.attempt_id);
      setRegisteredSubjects(reg.data.subjects || []);
      setMode("review");
    } catch (err) {
      // ✅ If backend blocks trials, show upgrade
      if (err?.response?.status === 403) {
        const code = err?.response?.data?.code;
        if (code === "CBT_SUBSCRIPTION_REQUIRED") {
          denyJambAccess(err.response?.data?.message);
          toast.error("Upgrade required for JAMB.");
          return;
        }
      }

      if (err.response?.status === 403 && err.response.data?.locked) {
        setLockedInfo({
          locked: true,
          next_week_start: err.response.data?.next_week_start || null,
        });
      }
      toast.error(err.response?.data?.message || "Registration failed.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ===================== NAVIGATION ===================== */
  const goToAnalysis = () => {
    if (attemptId) {
      navigate(`/dashboard/jamb-analysis/${attemptId}`, { replace: true });
    } else {
      navigate("/dashboard", { replace: true });
    }
  };

  // ✅ UPDATED: remove modal flow; start immediately on button click
  const startExam = async () => {
    // 🔒 Block if other exam types
    if (blockedByOtherExam) return;

    // ✅ PAID-ONLY guard
    if (jambAccessDenied) {
      toast.error("JAMB is for paid CBT subscribers only.");
      goToUpgrade();
      return;
    }

    // ✅ RESUME ACTIVE JAMB — DO NOT CALL /exam/start
    if (resumeInfo?.examId) {
      navigate(`/exam/jamb/session/${resumeInfo.examId}`, {
        replace: true,
      });
      return;
    }

    if (lockedInfo?.locked) {
      goToAnalysis();
      return;
    }

    try {
      setSubmitting(true);

      // 1️⃣ Ask backend what the next subject is
      const next = await api.post("/jamb/next-subject");

      if (next.data?.locked || next.data?.completed) {
        goToAnalysis();
        return;
      }

      if (!next.data?.subject_id) {
        goToAnalysis();
        return;
      }

      // 2️⃣ START NEW SUBJECT (only when no active exam)
      const start = await api.post("/exam/start", {
        subject_id: next.data.subject_id,
        exam_type: "JAMB",
      });

      navigate(`/exam/jamb/session/${start.data.exam.id}`, {
        replace: true,
      });
    } catch (err) {
      // ✅ If backend blocks trials, show upgrade
      if (err?.response?.status === 403) {
        const code = err?.response?.data?.code;
        if (code === "CBT_SUBSCRIPTION_REQUIRED") {
          denyJambAccess(err.response?.data?.message);
          toast.error("Upgrade required for JAMB.");
          return;
        }
      }

      if (err.response?.status === 409) {
        toast.info("Resuming your active JAMB exam…");
      } else {
        toast.error("Unable to start JAMB exam");
      }
    } finally {
      setSubmitting(false);
    }
  };

  /* ===================== CLEAR ===================== */
const clearRegistration = async () => {
  if (lockedInfo?.locked || resumeInfo) return;

  // Paid-only guard
  if (jambAccessDenied) {
    toast.error("JAMB is for paid CBT subscribers only.");
    goToUpgrade();
    return;
  }

  setClearing(true);        // 👈 START SPINNER
  setSubmitting(true);     // 👈 DISABLE START BUTTON

  try {
    await api.delete("/jamb/clear-registration");

    setRegisteredSubjects([]);
    setSelected([]);
    await loadSubjects();
    setMode("register");
  } catch (err) {
    toast.error("Failed to clear subjects.");
  } finally {
    setClearing(false);     // 👈 STOP SPINNER
    setSubmitting(false);
  }
};


  /* ===================== LOADING ===================== */
  if (mode === "loading" || jambAccessLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }



  /* ===================== RENDER ===================== */
  return (
    <Box maxWidth={900} mx="auto" p={2}>
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight={800} gutterBottom>
          🎯 JAMB Weekly Challenge
        </Typography>

        {/* ✅ NEW: PAID-ONLY ACCESS ALERT (blocks trials) */}
        {jambAccessDenied && (
          <Alert severity="error" sx={{ mb: 2 }}>
            🚫 <strong>JAMB is for paid users only.</strong>
            <br />
            {jambAccessMessage ||
              "You need an active CBT subscription to register or attempt JAMB."}
            <Divider sx={{ my: 1 }} />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button
                variant="contained"
                onClick={goToUpgrade}
                sx={{ fontWeight: 800 }}
              >
                Upgrade to CBT Plan
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/dashboard", { replace: true })}
              >
                Back to Dashboard
              </Button>
            </Stack>
          </Alert>
        )}

        {/* ✅ NEW: Blocking alert if active exam is SUBJECT/POST_UTME */}
        {blockedByOtherExam && (
          <Alert severity="error" sx={{ mb: 2 }}>
            🚫 <strong>Access restricted on this page.</strong>
            <br />
            You currently have an active{" "}
            <strong>{blockedExamType || "NON-JAMB"}</strong> exam.
            <br />
            Finish that exam from its own page before you can start/resume JAMB
            here.
          </Alert>
        )}

        {lockedInfo?.locked && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            🔒 <strong>You have completed this week’s JAMB.</strong>
            <br />
            Next attempt opens in <strong>{countdown.formatted}</strong>
          </Alert>
        )}

        {resumeInfo && !lockedInfo?.locked && !blockedByOtherExam && (
          <Alert severity="info" sx={{ mb: 2 }}>
            📘 <strong>{resumeInfo.subjectName}</strong>
            <br />
            📊 Subject {resumeInfo.subjectIndex} of{" "}
            {resumeInfo.totalSubjects}
            <br />
            ⏱️ Time Remaining:{" "}
            <strong>{formatTime(resumeInfo.remainingSeconds)}</strong>
          </Alert>
        )}

        {/* ===== REVIEW ===== */}
        {mode === "review" && (
          <>
            {registeredSubjects.map((s) => (
              <Typography key={s.id}>• {s.name}</Typography>
            ))}

            {/* ✅ MAIN BUTTON: disabled if SUBJECT/POST_UTME active OR JAMB access denied */}
          <Button
  fullWidth
  variant="contained"
  sx={{ mt: 3, fontWeight: 800 }}
  disabled={
    submitting ||
    clearing ||          // 👈 disable while clearing
    blockedByOtherExam ||
    jambAccessDenied
  }
  onClick={lockedInfo?.locked ? goToAnalysis : startExam}
  startIcon={
    (submitting || clearing) ? (
      <CircularProgress size={18} color="inherit" />
    ) : null
  }
>
  {clearing
    ? "Clearing Subjects…"
    : lockedInfo?.locked
    ? "View JAMB Analysis"
    : resumeInfo
    ? submitting
      ? "Continuing JAMB Exam…"
      : "Continue JAMB Exam"
    : submitting
    ? "Starting JAMB Exam…"
    : "Start JAMB Exam"}
</Button>


            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate("/dashboard/jamb/my-jamb-history")}
              >
                📘 My JAMB History
              </Button>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate("/dashboard/jamb/jamb-learderboard")}
              >
                🏆 JAMB Leaderboard
              </Button>
            </Stack>

            {/* Optional: Clear registration button remains available if you use it elsewhere */}
            {/* (We did not remove anything; leaving your function intact.) */}
			
{!jambAccessDenied &&
  registeredSubjects.length > 0 &&
  !lockedInfo?.locked &&
  !resumeInfo &&
  !blockedByOtherExam && (
    <Button
      fullWidth
      variant="text"
      color="error"
      sx={{ mt: 2, fontWeight: 700, textTransform: "none" }}
      disabled={clearing || submitting}
      onClick={clearRegistration}
    >
      {clearing ? "Clearing…" : "❌ Clear & Re-Register Subjects"}
    </Button>
)}

	
			
			
			
          </>
        )}

        {/* ===== REGISTER ===== */}
        {mode === "register" && !lockedInfo?.locked && (
          <>
            <Grid container spacing={1}>
              {subjects.map((s) => (
                <Grid item xs={12} sm={6} key={s.id}>
                  <Box display="flex" alignItems="center">
                    <Checkbox
                      checked={isEnglish(s) || selected.includes(s.id)}
                      disabled={
                        jambAccessDenied ||
                        isEnglish(s) ||
                        (selected.length >= 3 && !selected.includes(s.id))
                      }
                      onChange={() => toggle(s)}
                    />
                    <Typography>
                      {s.name} {isEnglish(s) ? "(Compulsory)" : ""}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

           <Button
  fullWidth
  variant="contained"
  sx={{ mt: 3, fontWeight: 800 }}
  disabled={
    jambAccessDenied ||
    selected.length !== 3 ||
    submitting ||
    clearing
  }
  onClick={register}
  startIcon={
    submitting ? (
      <CircularProgress size={18} color="inherit" />
    ) : null
  }
>
  {submitting ? "Registering Subjects…" : "Register for This Week"}
</Button>


            {/* If you already have a clear button in your design, you can wire it to clearRegistration() */}
            {/* We kept the clearRegistration logic without removing any code. */}
          </>
        )}
      </Paper>

      {/* =========================================================
         ❌ MODALS REMOVED (as requested)
         - START CONFIRM MODAL
         - NEXT SUBJECT MODAL
         Now: Start JAMB Exam button starts immediately.
      ========================================================= */}
    </Box>
  );
}
